import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@/lib/zodcompat'
import { toast } from 'sonner'
import { Dialog } from '@base-ui/react/dialog'
import {
  settleObligationFormSchema,
  type SettleObligationFormData,
} from '@/validators/financial-obligation'
import { useSettleObligation } from '@/hooks/useFinancialObligations'
import { useCashAccounts } from '@/hooks/useCashAccounts'
import { MoneyInput } from '@/components/form/money-input'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/ui/loading-button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type { FinancialObligation } from '@/types/financial-obligation'

const selectClass = cn(
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
)

function apiError(err: unknown, fallback: string) {
  return (
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? fallback
  )
}

function todayISO() {
  return new Date().toISOString().slice(0, 10)
}

interface Props {
  open: boolean
  onClose: () => void
  obligation: FinancialObligation | null
}

export function SettleObligationModal({ open, onClose, obligation }: Props) {
  const settle = useSettleObligation(obligation?.id ?? '')
  const { data: accountsData } = useCashAccounts({ active: true, perPage: 100 })
  const accounts = accountsData?.data ?? []

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<SettleObligationFormData>({
    resolver: zodResolver(settleObligationFormSchema),
    defaultValues: {
      paidAccountId: '',
      paidAmount: '' as unknown as number,
      paidAt: todayISO(),
      paidMethod: 'PIX',
      notes: '',
    },
  })

  useEffect(() => {
    if (open && obligation) {
      const fallback = accounts.find((a) => a.isDefault)?.id ?? accounts[0]?.id ?? ''
      reset({
        paidAccountId: fallback,
        paidAmount: Number.parseFloat(obligation.amount),
        paidAt: todayISO(),
        paidMethod: 'PIX',
        notes: '',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, obligation?.id])

  async function onSubmit(data: SettleObligationFormData) {
    if (!obligation) return
    try {
      await settle.mutateAsync({
        ...data,
        paidAt: new Date(data.paidAt).toISOString(),
        notes: data.notes || null,
      })
      toast.success(
        obligation.type === 'PAYABLE'
          ? 'Conta paga e lançada no caixa'
          : 'Recebimento lançado no caixa',
      )
      onClose()
    } catch (err) {
      toast.error(apiError(err, 'Erro ao quitar'))
    }
  }

  if (!obligation) return null

  return (
    <Dialog.Root open={open} onOpenChange={(v) => !v && onClose()}>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-40 bg-black/50" />
        <Dialog.Popup className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background p-6 shadow-md">
          <Dialog.Title className="text-lg font-semibold text-foreground">
            {obligation.type === 'PAYABLE' ? 'Pagar' : 'Receber'} O-{obligation.code}
          </Dialog.Title>
          <Dialog.Description className="text-sm text-muted-foreground">
            {obligation.description} — {obligation.type === 'PAYABLE' ? 'saída' : 'entrada'} no caixa.
          </Dialog.Description>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="st-account">Conta *</Label>
              <Controller
                name="paidAccountId"
                control={control}
                render={({ field }) => (
                  <select id="st-account" value={field.value} onChange={field.onChange} className={selectClass}>
                    <option value="" disabled>Selecione…</option>
                    {accounts.map((a) => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                )}
              />
              {errors.paidAccountId && (
                <p className="text-xs text-destructive">{errors.paidAccountId.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="st-amount">Valor *</Label>
                <MoneyInput id="st-amount" error={!!errors.paidAmount} {...register('paidAmount')} />
                {errors.paidAmount && (
                  <p className="text-xs text-destructive">{errors.paidAmount.message}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="st-date">Data *</Label>
                <Input id="st-date" type="date" aria-invalid={!!errors.paidAt} {...register('paidAt')} />
                {errors.paidAt && (
                  <p className="text-xs text-destructive">{errors.paidAt.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="st-method">Forma de pagamento *</Label>
              <Controller
                name="paidMethod"
                control={control}
                render={({ field }) => (
                  <select id="st-method" value={field.value} onChange={field.onChange} className={selectClass}>
                    <option value="PIX">PIX</option>
                    <option value="BANK_TRANSFER">Transferência</option>
                    <option value="BANK_SLIP">Boleto</option>
                    <option value="CASH">Dinheiro</option>
                    <option value="OTHER">Outro</option>
                  </select>
                )}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="st-notes">Observações</Label>
              <Input id="st-notes" {...register('notes')} />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={settle.isPending}>
                Cancelar
              </Button>
              <LoadingButton type="submit" loading={settle.isPending} loadingText="Quitando…">
                Confirmar quitação
              </LoadingButton>
            </div>
          </form>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
